import { useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'Constants/env';
import { FORCE_POLLING } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MessagesGetRequest } from 'Interfaces/apiDtos';
import { ResultsCollection } from 'Interfaces/axiosResponse';
import { runInAction } from 'mobx';
import { MobXProviderContext } from 'mobx-react';
import { ConversationModel } from 'Models/ConversationModel';
import { GroupedMessagesContainer } from 'Models/GroupedMessagesContainer';
import { IMessageModel, MessageModel } from 'Models/MessageModel';
import PersonModel from 'Models/PersonModel';
import { PollingConfig } from 'Providers/PollingProvider/index.types';
import { useContext, useState, useEffect } from 'react';
import { MessageStore } from 'Stores/MessageStore';
import { RootStoreProps } from 'Stores/RootStore.types';
import { getISOStringFromTimeUUID } from 'Utils/timeUUIDParser';
import API from '~/api';

export const QUERY_KEY_FETCH_MESSAGE = 'message.fetchMessage';
export const QUERY_KEY_FETCH_MESSAGES = 'messages.fetchMessages';
/**
 * Fetch messages for a conversation
 * @param conversationId - The ID of the conversation
 * @param messageStore - The message store
 * @returns The messages
 */
export async function fetchMessages(
  conversationId: ConversationModel['id'],
  messageStore: MessageStore,
  showCallMessagesInChat: boolean | undefined
) {
  if (!conversationId) {
    throw new Error('No conversation ID provided');
  }

  const msgsGet: MessagesGetRequest = {
    Limit: 31,
    ShowCallMessagesInChat: showCallMessagesInChat,
    ShowDeleted: true,
    SortDirection: 'Ascending',
  };

  // Get existing messages for the conversation
  const groupedMessages =
    messageStore.groupedMessagesByConversationMap.get(conversationId);

  if (!groupedMessages) {
    // If no messages exist yet, return an empty array, allow the existing code to load the messages
    return [];
  }

  const lastMessageId = groupedMessages.NewestMessageId;

  // If there is a last message, fetch new messages by skipping the last message
  if (lastMessageId) {
    try {
      const response = await API.get<ResultsCollection<IMessageModel>>(
        API_ENDPOINTS.ConversationMessagesAscending(conversationId, {
          ...msgsGet,
          SkipId: lastMessageId,
          SkipOver: true,
        })
      );

      const results = response.data?.results;

      if (results?.length > 0) {
        // Process new messages
        results.forEach((messageData) => {
          const newMessage = new MessageModel({
            ...messageData,
            created:
              messageData.created || getISOStringFromTimeUUID(messageData.id),
          });

          // Check if the message already exists in the grouped messages
          const existingMessage = groupedMessages.findMessageById(
            newMessage.id
          );

          if (
            !existingMessage &&
            (showCallMessagesInChat || !newMessage.call)
          ) {
            // Add the new message to the grouped messages
            groupedMessages.pushMessageDirect(newMessage);
          }
        });
      }
    } catch (error) {
      console.error('[fetchMessages] Error:', error);
    }
  }

  return groupedMessages;
}

/**
 * Fetch a message by conversationId and messageId
 * @param conversationId - The ID of the conversation
 * @param messageId - The ID of the message
 * @returns The message
 */
export const fetchMessage = async (
  conversationId: string,
  messageId: string
): Promise<IMessageModel | undefined> => {
  if (!conversationId || !messageId) {
    return undefined;
  }

  const { data } = await API.get<PersonModel>(
    API_ENDPOINTS.ConversationMessageById(conversationId, messageId)
  );

  return data;
};

/**
 * Fetch messages for a conversation
 * @param pollingEnabled - Whether polling is enabled
 * @param intervalFunction - The interval function
 * @returns The messages
 */
export const useFetchMessages = ({
  pollingEnabled,
  intervalFunction,
}: PollingConfig) => {
  const {
    conversation: { CurrentConversation },
    message: messageStore,
    preference: { preferences },
    participant: participantStore,
  } = useContext<RootStoreProps>(MobXProviderContext);

  const { [FORCE_POLLING]: forcePolling } = useFeatureFlags<boolean>([
    FORCE_POLLING,
  ]);

  const conversationId = CurrentConversation?.id;
  const [delayedConversations, setDelayedConversations] = useState<Set<string>>(
    new Set()
  );

  // Use useEffect to delay the first fetch until after the interval for each conversation
  useEffect(() => {
    if (
      pollingEnabled &&
      conversationId &&
      !delayedConversations.has(conversationId)
    ) {
      const timer = setTimeout(() => {
        setDelayedConversations((prev) => new Set(prev).add(conversationId));
      }, intervalFunction());

      return () => clearTimeout(timer);
    }
  }, [pollingEnabled, conversationId, delayedConversations, intervalFunction]);

  return useQuery({
    queryKey: [QUERY_KEY_FETCH_MESSAGES, conversationId],
    queryFn: async () => {
      if (!conversationId) {
        return null;
      }
      console.info(
        '[Polling] Fetching messages for conversation:',
        conversationId
      );

      try {
        const msgsGet: MessagesGetRequest = {
          Limit: 31,
          ShowCallMessagesInChat: preferences?.showCallMessagesInChat,
          ShowDeleted: true,
          SortDirection: 'Descending',
        };

        const response = await API.get<ResultsCollection<IMessageModel>>(
          API_ENDPOINTS.ConversationMessages(conversationId, msgsGet)
        );

        const results = response.data?.results;

        if (results && results.length > 0) {
          let groupedMessages =
            messageStore.groupedMessagesByConversationMap.get(conversationId);

          if (!groupedMessages) {
            runInAction(() => {
              groupedMessages = new GroupedMessagesContainer(
                conversationId,
                // eslint-disable-next-line @typescript-eslint/unbound-method
                results.map(MessageModel.FromResponseDto).reverse(),
                participantStore.selectLoggedInUserParticipantLastReadMessageId
              );
              messageStore.groupedMessagesByConversationMap.set(
                conversationId,
                groupedMessages
              );
            });
          } else {
            const messagesToAdd = results
              // eslint-disable-next-line @typescript-eslint/unbound-method
              .map(MessageModel.FromResponseDto)
              .reverse();

            const addedMessages: string[] = [];

            runInAction(() => {
              messagesToAdd.forEach((newMessage) => {
                const existingMessage = groupedMessages.findMessageById(
                  newMessage.id
                );
                if (
                  !existingMessage &&
                  (preferences?.showCallMessagesInChat || !newMessage.call)
                ) {
                  groupedMessages.pushMessageDirect(newMessage);
                  addedMessages.push(newMessage.id);
                }
              });
            });
          }
        }
        return (
          messageStore.groupedMessagesByConversationMap.get(conversationId) ||
          null
        );
      } catch (error) {
        if (forcePolling) {
          console.error('[useFetchMessages Polling] Error suppressed:', error);
          return (
            messageStore.groupedMessagesByConversationMap.get(conversationId) ||
            null
          );
        }
        throw error;
      }
    },
    enabled:
      pollingEnabled &&
      !!conversationId &&
      delayedConversations.has(conversationId),
    refetchInterval: delayedConversations.has(conversationId)
      ? intervalFunction
      : false,
    staleTime: 0,
    refetchOnMount: false,
  });
};

/**
 * Fetch a message by conversationId and messageId
 * @param conversationId - The ID of the conversation
 * @param messageId - The ID of the message
 * @returns The message
 */
export const useFetchMessage = (conversationId: string, messageId: string) =>
  useQuery({
    queryKey: [QUERY_KEY_FETCH_MESSAGE, conversationId, messageId],
    queryFn: async () => fetchMessage(conversationId, messageId),
  });

export const useMessage = () => ({
  useFetchMessage,
  useFetchMessages,
});
